import { useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import LinkExt from '@tiptap/extension-link';
import ImageExt from '@tiptap/extension-image';
import YoutubeExt from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Node } from '@tiptap/core';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, AlignLeft, AlignCenter, AlignRight, Link2, ImagePlus,
  Youtube, Minus, Eraser, Undo2, Redo2, Share2, Loader2, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const Embed = Node.create({
  name: 'socialEmbed',
  group: 'block',
  atom: true,
  addAttributes() {
    return { src: { default: null } };
  },
  parseHTML() {
    return [{ tag: 'div[data-social-embed] iframe' }];
  },
  renderHTML({ node }) {
    return [
      'div',
      { 'data-social-embed': '', class: 'article-embed' },
      ['iframe', { src: node.attrs.src, loading: 'lazy', allowfullscreen: 'true', referrerpolicy: 'no-referrer-when-downgrade' }],
    ];
  },
});

function toEmbedUrl(url) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { type: 'youtube', src: yt[1] };
  const fb = url.match(/facebook\.com\/(.+)/);
  if (fb) return { type: 'iframe', src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false` };
  const ig = url.match(/instagram\.com\/(p|reel|reels)\/([\w-]+)/);
  if (ig) return { type: 'iframe', src: `https://www.instagram.com/${ig[1] === 'p' ? 'p' : 'reel'}/${ig[2]}/embed` };
  const tk = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
  if (tk) return { type: 'iframe', src: `https://www.tiktok.com/embed/v2/${tk[1]}` };
  const tw = url.match(/(?:twitter\.com|x\.com)\/(\w+)\/status\/(\d+)/);
  if (tw) return { type: 'iframe', src: `https://platform.twitter.com/embed/Tweet.html?id=${tw[2]}` };
  return { type: 'iframe', src: url };
}

function ToolbarButton({ onClick, active, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
        active ? 'bg-cnib-navy text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange }) {
  const [dialog, setDialog] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      LinkExt.configure({ openOnClick: false }),
      ImageExt,
      YoutubeExt.configure({ width: 640, height: 360, nocookie: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Escribe el cuerpo de la nota… Usa la barra superior para citas, imágenes y videos.' }),
      Embed,
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { class: 'article-body min-h-[320px] px-5 py-4 outline-none', 'data-testid': 'article-body-editor' },
    },
  });

  const openDialog = (type) => {
    setUrlInput('');
    setDialog(dialog === type ? null : type);
  };

  const insertFromUrl = useCallback(() => {
    if (!editor || !urlInput.trim()) return;
    const url = urlInput.trim();
    if (dialog === 'link') {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else if (dialog === 'image') {
      editor.chain().focus().setImage({ src: url }).run();
    } else if (dialog === 'video') {
      const embed = toEmbedUrl(url);
      if (embed.type === 'youtube') {
        editor.chain().focus().setYoutubeVideo({ src: url }).run();
      } else {
        editor.chain().focus().insertContent({ type: 'socialEmbed', attrs: { src: embed.src } }).run();
      }
    }
    setDialog(null);
    setUrlInput('');
  }, [editor, dialog, urlInput]);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      editor.chain().focus().setImage({ src: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }).run();
      toast.success('Imagen insertada.');
      setDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la imagen.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (!editor) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white focus-within:border-cnib-blue focus-within:ring-2 focus-within:ring-cnib-blue/15">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 bg-slate-50 px-2 py-1.5">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Negrita" testid="editor-bold"><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Cursiva" testid="editor-italic"><Italic className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Subrayado" testid="editor-underline"><UnderlineIcon className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Tachado" testid="editor-strike"><Strikethrough className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Título H1" testid="editor-h1"><Heading1 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Título H2" testid="editor-h2"><Heading2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Título H3" testid="editor-h3"><Heading3 className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Viñetas" testid="editor-bullets"><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numerada" testid="editor-ordered"><ListOrdered className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Cita" testid="editor-quote"><Quote className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Código" testid="editor-code"><Code className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Izquierda" testid="editor-align-left"><AlignLeft className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centro" testid="editor-align-center"><AlignCenter className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Derecha" testid="editor-align-right"><AlignRight className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton onClick={() => openDialog('link')} active={editor.isActive('link')} title="Enlace" testid="editor-link"><Link2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => openDialog('image')} title="Imagen (subir o URL)" testid="editor-image"><ImagePlus className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => openDialog('video')} title="Video / red social (YouTube, Facebook, Instagram, TikTok, X)" testid="editor-video"><Youtube className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador" testid="editor-hr"><Minus className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Limpiar formato" testid="editor-clear"><Eraser className="h-4 w-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Deshacer" testid="editor-undo"><Undo2 className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Rehacer" testid="editor-redo"><Redo2 className="h-4 w-4" /></ToolbarButton>
      </div>

      {dialog && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-cnib-blue/5 px-3 py-2" data-testid="editor-url-dialog">
          {dialog === 'image' && (
            <label className={`flex cursor-pointer items-center gap-1.5 rounded-lg bg-cnib-blue px-3 py-1.5 text-xs font-bold text-white ${uploading ? 'opacity-60' : ''}`} data-testid="editor-image-upload">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
              Subir
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} />
            </label>
          )}
          {dialog === 'video' && <Share2 className="h-4 w-4 shrink-0 text-slate-400" />}
          <input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), insertFromUrl())}
            placeholder={dialog === 'link' ? 'https://enlace…' : dialog === 'image' ? 'https://imagen…' : 'URL de YouTube, Facebook, Instagram, TikTok o X…'}
            className="min-w-40 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-cnib-blue"
            data-testid="editor-url-input"
          />
          <button type="button" onClick={insertFromUrl} className="flex items-center gap-1 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-orange-600" data-testid="editor-url-insert">
            <Check className="h-3.5 w-3.5" /> Insertar
          </button>
          <button type="button" onClick={() => setDialog(null)} className="text-xs font-semibold text-slate-400 hover:text-slate-600">
            Cancelar
          </button>
        </div>
      )}

      <EditorContent editor={editor} />
    </div>
  );
}
